import { useEffect, useState } from 'react';
import type { EntityField } from '@ContractBuilder/contract-builder-header/constants';
import { useUIStore } from '@ContractBuilder/store/ui.store';

export type OnSaveFn<T = string> = (field: string, value: T, onCancel: () => void) => Promise<void>;

interface UseEditableFieldArgs<T = string> {
  defaultValue: T;
  exitEditModeAfterConfirm?: boolean;
  name: EntityField;
  onSave: OnSaveFn<T>;
}

interface EditableFieldState<T = string> {
  value: T;
  isEditing: boolean;
  isUpdating: boolean;
}

export const useEditableField = <T = string>({
  defaultValue,
  exitEditModeAfterConfirm = true,
  onSave,
  name,
}: UseEditableFieldArgs<T>) => {
  const { setActiveEditingField, activeEditingField } = useUIStore(({ setActiveEditingField, activeEditingField }) => ({
    setActiveEditingField,
    activeEditingField,
  }));

  const [state, setState] = useState<EditableFieldState<T>>({
    value: defaultValue,
    isEditing: activeEditingField === name,
    isUpdating: false,
  });

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      isEditing: activeEditingField === name,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEditingField]);

  useEffect(() => {
    if (!state.isEditing) {
      setState((prev) => ({ ...prev, value: defaultValue }));
    }
  }, [state.isEditing, defaultValue]);

  const handleClick = () => {
    setActiveEditingField(name);
  };

  const handleCancel = () => {
    setActiveEditingField(undefined);
  };

  const handleChange = (value: T) => {
    setState((prev) => ({ ...prev, value }));
  };

  const handleUpdate = async () => {
    setState((prev) => ({ ...prev, isUpdating: true }));
    try {
      await onSave(name, state.value, handleCancel);
    } finally {
      setActiveEditingField(undefined);
      setState((prev) => ({
        ...prev,
        isUpdating: false,
        isEditing: exitEditModeAfterConfirm ? false : prev.isEditing,
      }));
    }
  };

  return {
    onCancel: handleCancel,
    onChange: handleChange,
    onClick: handleClick,
    onUpdate: handleUpdate,
    state,
  };
};
